import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, HeartHandshake, Loader2, Tag, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function FPPlanos() {
  const { user } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [my, setMy] = useState(null);
  const [cycle, setCycle] = useState("monthly");
  const [saving, setSaving] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [couponInfo, setCouponInfo] = useState(null); // {code, discount_percent, remaining}
  const [couponError, setCouponError] = useState("");
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    api.get("/fp/plans/catalog").then((r) => setCatalog(r.data.plans)).catch(() => {});
    if (user) api.get("/fp/plans/me").then((r) => setMy(r.data)).catch(() => setMy(null));
  }, [user]);

  const validateCoupon = async () => {
    const code = coupon.trim().toUpperCase();
    if (!code) { setCouponError("Digite um código"); return; }
    setValidating(true);
    setCouponError("");
    try {
      const { data } = await api.get("/fp/plans/coupons/validate", { params: { code } });
      setCouponInfo(data);
      setCoupon(data.code);
      toast.success(`Cupom aplicado: ${data.discount_percent}% de desconto!`);
    } catch (e) {
      setCouponInfo(null);
      const msg = e?.response?.data?.detail || "Cupom inválido";
      setCouponError(msg);
      toast.error(msg);
    } finally { setValidating(false); }
  };

  const removeCoupon = () => {
    setCoupon("");
    setCouponInfo(null);
    setCouponError("");
  };

  const subscribe = async (plan) => {
    if (!user) return (window.location.href = "/entrar?next=/filho-presente/planos");
    setSaving(plan);
    try {
      const payload = { plan, cycle, origin_url: window.location.origin };
      if (couponInfo?.code) payload.coupon = couponInfo.code;
      const { data } = await api.post("/fp/plans/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar assinatura");
    } finally { setSaving(null); }
  };

  if (!catalog) return <div className="p-10 text-slate-500">Carregando planos…</div>;

  const applyDiscount = (v) => couponInfo ? v * (1 - couponInfo.discount_percent / 100) : v;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="fp-planos">
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-amber-700 font-bold">
          <HeartHandshake className="w-3.5 h-3.5" /> Filho Presente · Assinaturas
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#0B2B62] mt-2">
          Cuide de quem você ama, com plano fixo
        </h1>
        <p className="text-slate-600 mt-2 max-w-xl mx-auto text-sm">
          Assine e tenha organização, consultas, exames e transporte para seus idosos em um só lugar.
        </p>

        {/* Ciclo */}
        <div className="mt-6 inline-flex items-center gap-1 border border-slate-200 rounded-full p-1 bg-white">
          {[["monthly", "Mensal"], ["yearly", "Anual · economize"]].map(([k, l]) => (
            <button key={k} onClick={() => setCycle(k)} data-testid={`fp-plan-cycle-${k}`}
              className={`text-xs px-4 py-1.5 rounded-full transition-colors ${
                cycle === k ? "bg-[#0B2B62] text-white" : "text-slate-600 hover:bg-slate-50"
              }`}>{l}</button>
          ))}
        </div>

        {/* Cupom */}
        <div className="mt-6 max-w-md mx-auto">
          {couponInfo ? (
            <div data-testid="fp-plan-coupon-applied"
              className="flex items-center justify-between gap-3 px-4 py-2.5 bg-emerald-50 border-2 border-emerald-300 rounded-lg text-left">
              <div className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-emerald-700 shrink-0" />
                <div className="text-sm">
                  <b className="text-emerald-900">{couponInfo.code}</b>
                  <span className="text-emerald-800"> · {couponInfo.discount_percent}% off</span>
                  {couponInfo.description && (
                    <div className="text-[11px] text-emerald-700 mt-0.5">{couponInfo.description}</div>
                  )}
                </div>
              </div>
              <button onClick={removeCoupon} data-testid="fp-plan-coupon-remove"
                className="text-emerald-700 hover:text-emerald-900 shrink-0">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-stretch gap-2">
              <div className="relative flex-1">
                <Tag className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  value={coupon}
                  onChange={(e) => { setCoupon(e.target.value.toUpperCase()); setCouponError(""); }}
                  onKeyDown={(e) => e.key === "Enter" && validateCoupon()}
                  placeholder="Tem um cupom? Ex.: FUNDADORES30"
                  data-testid="fp-plan-coupon-input"
                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <button onClick={validateCoupon} disabled={validating || !coupon.trim()}
                data-testid="fp-plan-coupon-apply"
                className="px-4 py-2 bg-amber-400 text-[#0B2B62] rounded-md font-semibold text-sm hover:bg-amber-300 disabled:opacity-40 inline-flex items-center gap-1.5">
                {validating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Aplicar
              </button>
            </div>
          )}
          {couponError && (
            <div data-testid="fp-plan-coupon-error"
              className="mt-1.5 text-xs text-red-600 text-left">{couponError}</div>
          )}
        </div>
      </div>

      {my?.plan && my.status === "active" && (
        <div className="mb-6 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4 flex items-center gap-3">
          <CheckCircle2 className="w-6 h-6 text-emerald-600" />
          <div className="text-sm text-emerald-900">
            <b>Plano ativo:</b> {catalog[my.plan]?.label} · {my.cycle === "monthly" ? "Mensal" : "Anual"}
            {my.expires_at && <span className="text-emerald-800"> · renova em {new Date(my.expires_at).toLocaleDateString("pt-BR")}</span>}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {Object.entries(catalog).map(([id, p]) => {
          const originalValue = cycle === "monthly" ? p.monthly_brl : p.yearly_brl;
          const finalValue = applyDiscount(originalValue);
          const isTotal = id === "cuidado_total";
          const active = my?.plan === id && my?.status === "active";
          return (
            <div key={id} data-testid={`fp-plan-${id}`}
              className={`relative bg-white border-2 rounded-2xl p-6 sm:p-8 transition-all ${
                isTotal ? "border-amber-400 shadow-lg shadow-amber-100" : "border-slate-200"
              }`}>
              {isTotal && (
                <div className="absolute -top-3 left-6 bg-amber-400 text-[#0B2B62] text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Mais popular
                </div>
              )}
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{p.label}</div>
              <div className="mt-3 flex items-baseline gap-2">
                {couponInfo && (
                  <div className="text-lg line-through text-slate-400" data-testid={`fp-plan-${id}-original`}>
                    R$ {originalValue.toFixed(2).replace(".", ",")}
                  </div>
                )}
                <div className={`font-display font-bold text-4xl ${couponInfo ? "text-emerald-700" : "text-[#0B2B62]"}`}>
                  R$ {finalValue.toFixed(2).replace(".", ",")}
                </div>
                <div className="text-sm text-slate-500">/{cycle === "monthly" ? "mês" : "ano"}</div>
              </div>
              {couponInfo && (
                <div className="mt-1 text-xs text-emerald-700 font-semibold">
                  Economia de R$ {(originalValue - finalValue).toFixed(2).replace(".", ",")} com {couponInfo.code}
                </div>
              )}
              {cycle === "yearly" && !couponInfo && (
                <div className="mt-1 text-xs text-emerald-700 font-semibold">
                  Equivale a R$ {(p.yearly_brl / 12).toFixed(2).replace(".", ",")}/mês (2 meses grátis)
                </div>
              )}
              <ul className="mt-6 space-y-2.5">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => subscribe(id)} disabled={saving || active}
                data-testid={`fp-plan-cta-${id}`}
                className={`mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-sm transition-colors ${
                  active ? "bg-emerald-100 text-emerald-800 cursor-default"
                  : isTotal ? "bg-amber-400 text-[#0B2B62] hover:bg-amber-300"
                  : "bg-[#0B2B62] text-white hover:bg-slate-800"
                } disabled:opacity-60`}>
                {saving === id && <Loader2 className="w-4 h-4 animate-spin" />}
                {active ? "Plano ativo" : "Assinar agora"}
              </button>
              <div className="text-[10px] text-slate-400 mt-2 text-center">
                Pagamento Stripe · cancele quando quiser
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-center text-xs text-slate-500">
        Dúvidas? Fale com a Central RAF pelo <a href="/preciso-de-ajuda" className="text-[#0B2B62] font-semibold underline">Preciso de ajuda</a>.
      </div>
    </div>
  );
}
